import React, { useState, useEffect } from 'react';
import { Table } from '../components/ui/Table';
import { X, ChevronDown } from 'lucide-react';
import { listFeedback } from '../services/resortAdminApi';

interface FeedbackItem {
  rating_id: string;
  score: string;
  comment: string;
  source_channels: string[];
  rating_date: string;
  order_id: string;
  customer_name: string;
  contact_email: string;
  contact_phone: string;
  custom_restaurant: string;
  custom_grocery: string;
}

const DATE_RANGE_OPTIONS = [
  { value: '', label: 'All Time' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'last_90_days', label: 'Last 90 Days' },
];

const RATING_OPTIONS = [
  { value: '', label: 'All Ratings' },
  { value: '1', label: '★ 1' },
  { value: '2', label: '★ 2' },
  { value: '3', label: '★ 3' },
  { value: '4', label: '★ 4' },
  { value: '5', label: '★ 5' },
];

export const Feedback: React.FC = () => {
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  const [ratingFilter, setRatingFilter] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState('');
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);

  const buildFilters = () => {
    const filters: Record<string, any> = {};

    if (ratingFilter) {
      filters.rating = [Number(ratingFilter)];
    }

    if (dateRangeFilter) {
      const now = new Date();
      let days = 0;
      if (dateRangeFilter === 'last_7_days') days = 7;
      else if (dateRangeFilter === 'last_30_days') days = 30;
      else if (dateRangeFilter === 'last_90_days') days = 90;

      if (days > 0) {
        const startDate = new Date();
        startDate.setDate(now.getDate() - days);
        filters.start_date = startDate.toISOString().split('T')[0];
        filters.end_date = now.toISOString().split('T')[0];
      }
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  };

  const loadFeedback = async () => {
    setLoading(true);
    setError('');
    try {
      const filters = buildFilters();
      const result = await listFeedback(filters, page, pageSize);
      if (result) {
        setFeedbacks(result.data || []);
        setTotal(result.total || 0);
      }
    } catch (err: any) {
      console.error('Error loading feedback:', err);
      setError(err.message || 'Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFeedback();
  }, [page, pageSize, ratingFilter, dateRangeFilter]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const columns = [
    {
      key: 'customer_name',
      header: 'User',
      render: (_: any, row: FeedbackItem) => (
        <span className="font-medium text-gray-900">
          {row.customer_name || row.contact_email || '—'}
        </span>
      ),
    },
    {
      key: 'score',
      header: 'Rating',
      render: (value: string) => (
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }, (_, i) => (
            <span
              key={i}
              className={`text-sm ${i < Number(value) ? 'text-orange-500' : 'text-gray-300'}`}
            >
              {'★'}
            </span>
          ))}
        </div>
      ),
    },
    {
      key: 'comment',
      header: 'Comment',
      render: (value: string) => (
        <span
          className="text-gray-600 block truncate"
          style={{ maxWidth: 200 }}
          title={value || ''}
        >
          {value || '—'}
        </span>
      ),
    },
    {
      key: 'source_channels',
      header: 'Sources',
      render: (value: string[]) => {
        if (!value || value.length === 0) return <span className="text-gray-400">{'—'}</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {value.map((ch) => (
              <span
                key={ch}
                className="px-2 py-0.5 text-xs rounded-full bg-gray-100 text-gray-800"
              >
                {ch}
              </span>
            ))}
          </div>
        );
      },
    },
    {
      key: 'rating_date',
      header: 'Date',
      render: (value: string) => (
        <span className="text-gray-600">{formatDate(value)}</span>
      ),
    },
    {
      key: 'rating_id',
      header: '',
      render: (_: any, row: FeedbackItem) => (
        <button
          onClick={() => setSelectedFeedback(row)}
          className="text-xs text-alps-primary hover:underline"
        >
          View
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Feedback</h1>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-row gap-4 mb-6">
          {/* Rating Filter */}
          <div className="relative">
            <select
              value={ratingFilter}
              onChange={(e) => {
                setRatingFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {RATING_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Date Range Filter */}
          <div className="relative">
            <select
              value={dateRangeFilter}
              onChange={(e) => {
                setDateRangeFilter(e.target.value);
                setPage(1);
              }}
              className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-alps-primary focus:border-alps-primary"
            >
              {DATE_RANGE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Feedback Table */}
      <Table
        columns={columns}
        data={feedbacks}
        isLoading={loading}
        emptyMessage="No feedback yet"
        pagination={{
          page,
          page_size: pageSize,
          total,
        }}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        rowClassName="cursor-pointer"
        cellClassName=""
      />

      {/* Detail Modal */}
      {selectedFeedback && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
          onClick={() => setSelectedFeedback(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Feedback Details</h3>
              <button
                onClick={() => setSelectedFeedback(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              {/* Rating */}
              <div>
                <p className="text-xs font-medium text-gray-500 mb-1">Rating</p>
                <div className="flex items-center gap-0.5">
                  {Array.from({ length: 5 }, (_, i) => (
                    <span
                      key={i}
                      className={`text-xl ${i < Number(selectedFeedback.score) ? 'text-orange-500' : 'text-gray-300'}`}
                    >
                      {'★'}
                    </span>
                  ))}
                  <span className="ml-2 text-sm text-gray-600">
                    {selectedFeedback.score} / 5
                  </span>
                </div>
              </div>

              {/* Comment */}
              {selectedFeedback.comment && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Comment</p>
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedFeedback.comment}
                  </p>
                </div>
              )}

              {/* Source Channels */}
              {selectedFeedback.source_channels &&
                selectedFeedback.source_channels.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 mb-1">
                      How did they find Mountly?
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {selectedFeedback.source_channels.map((ch) => (
                        <span
                          key={ch}
                          className="px-2.5 py-1 text-xs rounded-full bg-gray-100 text-gray-800"
                        >
                          {ch}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {/* Customer Info */}
              {(selectedFeedback.customer_name ||
                selectedFeedback.contact_email ||
                selectedFeedback.contact_phone) && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Customer</p>
                  {selectedFeedback.customer_name && (
                    <p className="text-sm text-gray-700">{selectedFeedback.customer_name}</p>
                  )}
                  {selectedFeedback.contact_email && (
                    <p className="text-xs text-gray-500">{selectedFeedback.contact_email}</p>
                  )}
                  {selectedFeedback.contact_phone && (
                    <p className="text-xs text-gray-500">{selectedFeedback.contact_phone}</p>
                  )}
                </div>
              )}

              {/* Order Link */}
              {selectedFeedback.order_id && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Order</p>
                  <a
                    href={`/orders?search=${encodeURIComponent(selectedFeedback.order_id)}`}
                    className="text-sm text-alps-primary hover:underline"
                  >
                    {selectedFeedback.order_id}
                  </a>
                </div>
              )}

              {/* Merchant */}
              {(selectedFeedback.custom_restaurant || selectedFeedback.custom_grocery) && (
                <div>
                  <p className="text-xs font-medium text-gray-500 mb-1">Merchant</p>
                  <p className="text-sm text-gray-700">
                    {selectedFeedback.custom_restaurant || selectedFeedback.custom_grocery}
                  </p>
                </div>
              )}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={() => setSelectedFeedback(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
